import { inject, NgModule } from "@angular/core";
import { NoPreloading, RouterModule, Routes } from "@angular/router";

import {
  CanAccessAuthenticated,
  CanAccessEWiz,
  CanAccessEdgar,
  CanAccessHSM,
  CanAccessIntelligent
} from "./shared/guards/can-activate.guards";

import { HomeComponent } from "./components/home/home.component";
import { PageNotFoundComponent } from "./components/page-not-found/page-not-found.component";
import { VersionComponent } from "./components/version/version.component";
import { OTA_KEY_DERIVATION_SCREEN_VISIBLE } from "./shared/constants/feature-flags.constants";

/**
 * The app's top-level routes.
 *
 * @remarks `securedata/derivation` resolves to Page Not Found unless {@link OTA_KEY_DERIVATION_SCREEN_VISIBLE}
 * is on, so a typed-in URL cannot reach the hidden Key Derivation screen.
 */
export const routes: Routes = [
  { path: "", redirectTo: "/esim/profiles", pathMatch: "full" },
  { path: "home", component: HomeComponent },
  { path: "version", component: VersionComponent },
  {
    path: "auth",
    loadChildren: () => import("./modules/auth/auth.module").then((module) => module.AuthModule)
  },
  {
    path: "applets",
    canActivate: [CanAccessAuthenticated, CanAccessEWiz],
    loadChildren: () => import("@ripsim/applet-warehouse").then((module) => module.appletWarehouseRoutes)
  },
  {
    path: "edgar/jobs",
    canActivate: [CanAccessAuthenticated, CanAccessEdgar],
    loadChildren: () => import("@ripsim/orders").then((module) => module.ordersRoutes)
  },
  {
    path: "edgar/manage",
    canActivate: [CanAccessAuthenticated, CanAccessEdgar],
    loadChildren: () => import("@ripsim/edgar-job-templates").then((module) => module.edgarJobTemplateRoutes)
  },
  {
    path: "edgar/files",
    canActivate: [CanAccessAuthenticated, CanAccessEdgar],
    loadChildren: () => import("./modules/edgar/edgar.module").then((module) => module.EdgarModule)
  },
  {
    path: "esim",
    canActivate: [CanAccessAuthenticated, CanAccessEWiz],
    loadChildren: () => import("./modules/esim/esim.module").then((module) => module.ESimModule)
  },
  {
    path: "securedata/transportkeys",
    canActivate: [CanAccessAuthenticated, CanAccessHSM],
    loadChildren: () => import("@ripsim/transport-keys").then((module) => module.transportKeysRoutes)
  },
  {
    path: "securedata/pgpkeys",
    canActivate: [CanAccessAuthenticated, CanAccessHSM],
    loadChildren: () => import("@ripsim/pgp-keys").then((module) => module.pgpKeysRoutes)
  },
  {
    path: "securedata/derivation",
    canMatch: [() => inject(OTA_KEY_DERIVATION_SCREEN_VISIBLE)],
    canActivate: [CanAccessAuthenticated, CanAccessHSM],
    loadChildren: () => import("@ripsim/key-derivation").then((module) => module.keyDerivationRoutes)
  },
  { path: "securedata/derivation", component: PageNotFoundComponent },
  {
    path: "securedata",
    canActivate: [CanAccessAuthenticated, CanAccessHSM],
    loadChildren: () => import("./modules/secure-data/secure-data.module").then((module) => module.SecureDataModule)
  },
  {
    path: "settings",
    canActivate: [CanAccessAuthenticated, CanAccessHSM],
    children: [
      {
        path: "destination",
        loadChildren: () => import("@ripsim/destinations").then((module) => module.DESTINATION_CHILD_ROUTES)
      },
      {
        path: "intelligent-variable-spec",
        canActivate: [CanAccessIntelligent],
        loadChildren: () => import("@ripsim/intelligent-variable-spec").then((module) => module.INTELLIGENT_VARIABLE_SPEC_ROUTES)
      },
      {
        path: "intelligent-asn-template",
        canActivate: [CanAccessIntelligent],
        loadChildren: () => import("@ripsim/intelligent-asn-template").then((module) => module.INTELLIGENT_ASN_TEMPLATE_ROUTES)
      },
      {
        path: "output-configurator",
        children: [
          {
            path: "output-file-definitions",
            loadChildren: () => import("@ripsim/output-file-configurator").then((module) => module.OUTPUT_FILE_CONFIGURATOR_ROUTES)
          },
          {
            path: "output-packages",
            loadChildren: () => import("@ripsim/output-package-configurator").then((module) => module.OUTPUT_PACKAGE_CONFIGURATOR_ROUTES)
          }
        ]
      },
      {
        path: "",
        loadChildren: () => import("@ripsim/output-file").then((module) => module.OUTPUT_FILE_ROUTES)
      },
      {
        path: "",
        loadChildren: () => import("@ripsim/input-configurator").then((module) => module.INPUT_CONFIGURATOR_ROUTES)
      }
    ]
  },
  { path: "**", component: PageNotFoundComponent }
];

@NgModule({
  imports: [
    RouterModule.forRoot(routes, {
      preloadingStrategy: NoPreloading,
      useHash: false,
      bindToComponentInputs: true
    })
  ],
  exports: [RouterModule]
})
// eslint-disable-next-line @typescript-eslint/no-extraneous-class
export class AppRoutingModule {}
